/**
 * Helper del rail (SideBar) — ACHIEVERY-1363.
 * Mapea el bloque destacado (SidebarHighlight) y el doodle (logo/fondo) desde el
 * documento singleton `clc_new_home_page`. Ambos son GLOBALES (iguales para todos los CLCs).
 * Compartido por la Home y las páginas de noticias para que el <SideBar> se vea igual en todas.
 */
import { getCleanPrismicImageUrl, renderRichText } from './prismicHelpers';

// Custom type (single) de la home. Fuente única para las 3 páginas (home + news list + detail).
export const CLC_HOME_PAGE_TYPE = 'clc_new_home_page';

/**
 * ¿El HTML tiene texto real? Prismic devuelve un párrafo vacío cuando el campo está sin
 * cargar, y el rich text puede traer solo tags o &nbsp;.
 * @param {String} html
 * @returns {Boolean}
 */
export function hasContent(html) {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
}

/**
 * @param {Object} data - `response.data` del documento clc_new_home_page.
 * @returns {{ highlight: Object, dynamicSidebar: Object }}
 *   highlight: props para SidebarHighlight (template, textHtml, ctaLabel, ctaUrl, videoType, videoId...).
 *   dynamicSidebar: { logoSrc, bgImage } para el doodle (vacío -> assets por defecto).
 */
export function mapSidebarData(data) {
  // --- Highlight ---
  let highlight = { template: 'empty' };
  // renderRichText devuelve un <p> por párrafo. El componente usa un único <p> inline, así que
  // respetamos los saltos hechos en Prismic convirtiéndolos en <br>:
  //   - Enter (párrafo nuevo)      -> límite </p><p>       -> <br>
  //   - Shift+Enter (salto suave)  -> \n dentro del texto  -> <br>
  // y quitamos los <p> que envuelven.
  const textHtml = renderRichText(data?.highlight_text)
    .replace(/<\/p>\s*<p>/gi, '<br>')
    .replace(/<\/?p>/gi, '')
    .replace(/\n/g, '<br>');
  // El toggle puede estar en ON con los campos sin cargar (ej. la locale es-es): sin texto
  // no hay panel que mostrar, así que cae a 'empty' y el componente no renderiza.
  if (data?.highlight_on_off && hasContent(textHtml)) {
    // highlight_cta_type (Select en Prismic): 'none' | 'link' | 'video' -> template del componente.
    const ctaType = data?.highlight_cta_type || 'none';
    const ctaLabel = data?.highlight_cta_label || '';
    const ctaUrl = data?.highlight_cta_link?.url || '';
    const videoId = data?.highlight_video_id || '';
    // Un CTA sin label (o sin destino) queda como pill vacío: en ese caso solo texto.
    let template = 'text';
    if (ctaLabel && ctaType === 'link' && ctaUrl) template = 'text-link';
    if (ctaLabel && ctaType === 'video' && videoId) template = 'text-video';
    // Interno vs externo se infiere por la composición de la URL (no por el target de Prismic):
    //   - Absoluta (http:// o https://) -> externo -> abre en nueva pestaña.
    //   - Relativa (ej. "/en/algo") -> interno -> misma pestaña.
    const isExternalUrl = /^https?:\/\//i.test(ctaUrl);
    highlight = {
      template,
      textHtml,
      ctaLabel,
      ctaUrl: ctaUrl || '#',
      ctaTarget: isExternalUrl ? '_blank' : '_self',
      videoType: data?.highlight_video_type || 'jwplayer',
      videoId
    };
  }

  // --- Doodle (logo + fondo) ---
  const dynamicSidebar = data?.sidebar_doodle_on_off
    ? {
        logoSrc: getCleanPrismicImageUrl(data?.sidebar_logo?.url),
        bgImage: getCleanPrismicImageUrl(data?.sidebar_background?.url)
      }
    : { logoSrc: '', bgImage: '' };

  return { highlight, dynamicSidebar };
}

// Cache en sessionStorage del branding del rail (global). Sirve para hidratar el SideBar
// sincrónicamente al navegar (home -> noticias) y evitar el flash del fondo default.
// Se cachea por idioma porque el highlight es texto localizado.
const SIDEBAR_CACHE_KEY = 'clcSidebarBranding';

export function saveSidebarCache(lang, data) {
  try {
    const all = JSON.parse(sessionStorage.getItem(SIDEBAR_CACHE_KEY) || '{}');
    all[lang] = data;
    sessionStorage.setItem(SIDEBAR_CACHE_KEY, JSON.stringify(all));
  } catch (e) {
    // sessionStorage puede fallar (modo privado, cuota); no es crítico.
  }
}

export function loadSidebarCache(lang) {
  try {
    const all = JSON.parse(sessionStorage.getItem(SIDEBAR_CACHE_KEY) || '{}');
    return all[lang] || null;
  } catch (e) {
    return null;
  }
}

// Precarga una imagen y resuelve cuando terminó de bajar (o falló). Sirve para no levantar
// el loader hasta que el fondo/logo del rail estén listos y no flashee el color de fondo.
export function preloadImage(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve();
      return;
    }
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}
